import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Divider,
} from "@mui/material";

const Home = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      {/* Hero Section */}
      <Box
        sx={{
          backgroundImage: "url(/hero2.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          color: "white",
          py: { xs: 8, md: 12 },
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(68, 119, 55, 0.25)",
            zIndex: 0,
          },
          minHeight: { xs: "400px", md: "500px" },
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: "2.5rem", md: "3.5rem" },
              }}
            >
              Semear é organizar o cuidado.
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                opacity: 0.95,
                maxWidth: "800px",
                mx: "auto",
                fontSize: { xs: "1.1rem", md: "1.5rem" },
              }}
            >
              Aqui você encontra as ferramentas pra criar, gerir e fortalecer
              sua feira agroecológica dentro da rede Multifeiras.
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
            >
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate("/login")}
                sx={{
                  bgcolor: "white",
                  color: "primary.main",
                  px: 4,
                  py: 1.5,
                  fontSize: "1.1rem",
                  "&:hover": {
                    bgcolor: "rgba(255, 255, 255, 0.9)",
                    transform: "translateY(-2px)",
                    boxShadow: 4,
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Acessar minha feira
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Sobre o papel da semeadora */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography
            variant="h3"
            component="h2"
            sx={{ fontWeight: 700, color: "primary.main", mb: 2 }}
          >
            As semeadoras são o coração da rede.
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: "text.secondary", maxWidth: "900px", mx: "auto" }}
          >
            São pessoas ou coletivos que organizam o escoamento da produção
            agroecológica no seu território — conectando agricultoras, parceiras
            urbanas e pontos de entrega.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                border: "2px solid",
                borderColor: "primary.main",
                borderRadius: 3,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 4,
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography
                  variant="h4"
                  component="div"
                  sx={{ mb: 2, fontSize: "3rem" }}
                >
                  🌾
                </Typography>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Cuidam dos pedidos, das entregas, da comunicação e da relação
                  com quem produz.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                border: "2px solid",
                borderColor: "secondary.main",
                borderRadius: 3,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 4,
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography
                  variant="h4"
                  component="div"
                  sx={{ mb: 2, fontSize: "3rem" }}
                >
                  💚
                </Typography>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  E, acima de tudo, ajudam a cultivar vínculos de solidariedade
                  e confiança.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Ferramentas da plataforma */}
      <Box sx={{ bgcolor: "white", py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{ fontWeight: 700, color: "primary.main", mb: 2 }}
            >
              Tudo que você precisa pra fazer a feira acontecer
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {[
              {
                icon: "📋",
                text: "Gerenciar pedidos e acompanhar pagamentos",
              },
              {
                icon: "📅",
                text: "Definir datas de abertura e fechamento",
              },
              {
                icon: "🥬",
                text: "Publicar produtos e ajustar preços",
              },
              {
                icon: "🚚",
                text: "Organizar entregas e pontos de retirada",
              },
              {
                icon: "💰",
                text: "Acompanhar o movimento financeiro da feira",
              },
              {
                icon: "👥",
                text: "Gerenciar encomendas de agricultoras",
              },
            ].map((tool, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <CardContent sx={{ p: 3, textAlign: "center" }}>
                    <Typography
                      variant="h3"
                      component="div"
                      sx={{ mb: 2, fontSize: "3rem" }}
                    >
                      {tool.icon}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {tool.text}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Propósito e sentido */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Card
          elevation={0}
          sx={{
            background: "linear-gradient(135deg, #9fd236 0%, #82a221 100%)",
            color: "white",
            borderRadius: 4,
            p: { xs: 4, md: 6 },
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{ fontWeight: 700, mb: 3 }}
            >
              A Multifeiras nasceu da prática da ajuda mútua.
            </Typography>
            <Typography variant="h6" sx={{ mb: 3, opacity: 0.95 }}>
              Acreditamos que redes de alimento podem ser também redes de apoio,
              aprendizado e autonomia.
            </Typography>
            <Typography variant="h6" sx={{ mb: 3, opacity: 0.95 }}>
              Ser semeadora é construir coletivamente um modo justo de
              distribuir comida e renda.
            </Typography>
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, mt: 4, fontSize: "1.5rem" }}
            >
              🍅 É plantar uma economia solidária, uma entrega de cada vez.
            </Typography>
          </Box>
        </Card>
      </Container>

      {/* Aprendizados e apoio */}
      <Box sx={{ bgcolor: "white", py: 8 }}>
        <Container maxWidth="lg">
          <Card
            elevation={0}
            sx={{
              border: "2px solid",
              borderColor: "secondary.main",
              borderRadius: 4,
              p: { xs: 4, md: 6 },
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h4"
                component="h2"
                sx={{ fontWeight: 700, color: "primary.main", mb: 3 }}
              >
                Ninguém semeia sozinha.
              </Typography>
              <Typography variant="h6" sx={{ color: "text.secondary", mb: 4 }}>
                Aqui você encontra materiais e orientações sobre:
              </Typography>

              <Grid container spacing={2} sx={{ mb: 4 }}>
                {[
                  "📢 Boas práticas de comunicação e logística",
                  "🤝 Dicas de relacionamento com agricultoras",
                  "🌱 Experiências de outras feiras da rede",
                ].map((item, idx) => (
                  <Grid item xs={12} md={4} key={idx}>
                    <Typography variant="body1" sx={{ fontWeight: 500, py: 2 }}>
                      {item}
                    </Typography>
                  </Grid>
                ))}
              </Grid>

              <Button
                variant="contained"
                size="large"
                sx={{ px: 4 }}
                onClick={() => {
                  window.open("https://multifeiras.com.br", "_blank");
                }}
              >
                Acessar materiais de apoio
              </Button>
            </Box>
          </Card>
        </Container>
      </Box>

      {/* Convite à rede */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography
            variant="h4"
            component="h2"
            sx={{ fontWeight: 700, color: "primary.main", mb: 3 }}
          >
            Quer que mais gente se envolva?
          </Typography>
        </Box>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            {
              icon: "🌿",
              text: "Convide agricultoras e pontos de entrega da sua região",
            },
            {
              icon: "🌻",
              text: "Compartilhe aprendizados com outras semeadoras",
            },
            {
              icon: "🌾",
              text: "Fortaleça a autogestão da rede",
            },
          ].map((item, idx) => (
            <Grid item xs={12} md={4} key={idx}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: 3, textAlign: "center" }}>
                  <Typography
                    variant="h3"
                    component="div"
                    sx={{ mb: 2, fontSize: "3rem" }}
                  >
                    {item.icon}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {item.text}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Rodapé */}
      <Box
        sx={{
          bgcolor: "primary.main",
          color: "white",
          py: 6,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
              Multifeiras — redes que cuidam do alimento e de quem cultiva.
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={3}
              justifyContent="center"
              sx={{ mb: 3 }}
            >
              <Button
                variant="text"
                sx={{
                  color: "white",
                  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.1)" },
                }}
                onClick={() => {
                  window.open("https://multifeiras.com.br", "_blank");
                }}
              >
                Sobre a rede
              </Button>
              <Button
                variant="text"
                sx={{
                  color: "white",
                  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.1)" },
                }}
                onClick={() => {
                  window.open("https://multifeiras.com.br/contato", "_blank");
                }}
              >
                Contato
              </Button>
              <Button
                variant="text"
                sx={{
                  color: "white",
                  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.1)" },
                }}
                onClick={() => {
                  window.open("https://multifeiras.com.br", "_blank");
                }}
              >
                Materiais de apoio
              </Button>
            </Stack>

            <Divider sx={{ bgcolor: "rgba(255, 255, 255, 0.2)", my: 3 }} />

            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Construído coletivamente por agricultoras, semeadoras e parceiras
              urbanas. 🌱
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
